<template>
	<Modals
		:title="'Details'"
		:display="true"
		@show="close"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<div class="detail-update">{{ actionDetails }}</div>
			<Table
				class="table-container--role-detail-history"
				:sourceObject="rolLogData"
				:tableMobilev1="true"
				@change="changedRolePagination"
			>
				<template #col-group>
					<colgroup>
						<col
							:class="[
								'table__col table__col--setting',
								isInternalRole && ' internal-role',
								isExternalRole && ' external-role'
							]"
						/>
						<col
							:class="[
								'table__col table__col--change',
								isInternalRole && ' internal-role',
								isExternalRole && ' external-role'
							]"
						/>
					</colgroup>
				</template>
				<template #body-changedPropertyName="{body}">
					<div class="role-detail">
						<span
							class="role-detail__changed-property-name"
							v-html="body.changedPropertyName"
						/>
					</div>
				</template>
				<template #body-newValue="{body}">
					<div class="role-detail">
						<span
							class="role-detail__new-value"
							v-html="body.newValue"
							data-test="newValue"
						/>
					</div>
				</template>
			</Table>
		</template>
		<template #modal-footer>
			<Button
				id="linkButton"
				className="btn--primary"
				labelText="Close"
				:btnClick="close"
			/>
		</template>
	</Modals>
</template>

<script>
import Modals from '@/components/shells/modal/modals';
import Button from '@/components/forms/button/button';
import Table from '@/components/shells/table/table';
import DateTimeMixin from '@/mixins/datetime-mixin';
import { updatePagination } from '@/utils/object';
import _ from 'lodash';

export default {
	name: 'LogRolHistoryDetailModal',
	components: {
		Modals,
		Button,
		Table
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		parentTag: {
			type: String,
			default: 'div'
		},
		display: {
			type: Boolean,
			default: false
		},
		logs: {
			type: Object,
			default: () => {}
		}
	},
	mixins: [DateTimeMixin],
	data() {
		return {
			detailHeadersHistory: [
				{
					key: 'changedPropertyName',
					label: 'Setting',
					sorteable: false
				},
				{
					key: 'newValue',
					label: 'Change',
					sorteable: false
				}
			],
			rolLogData: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			page: 1,
			pageSize: 10,
			loading: false
		};
	},
	created() {
		this.rolLogData.headers = this.detailHeadersHistory;
		this.fetchDetailRole();
	},
	computed: {
		actionDetails() {
			if (_.isEmpty(this.logs)) {
				return '';
			}
			let name = this.logs.changedByUserName
				.split(' ')
				.map(el => el.charAt(0).toUpperCase() + el.slice(1))
				.join(' ');
			return `${this.logs.changedAction}d by ${name} on ${this.logs.timestamp}`;
		},
		isInternalRole() {
			return this.logs.data.items[1].ClientId === 'vue-support-portal';
		},
		isExternalRole() {
			return !this.isInternalRole;
		}
	},
	methods: {
		fetchDetailRole() {
			const newSource = updatePagination(
				this.page,
				this.pageSize,
				_.cloneDeep(this.formatLogs())
			);
			this.rolLogData.source = newSource;
		},
		changedRolePagination(value) {
			this.page = value.page;
			this.pageSize = value.countPerPage;
		},
		close() {
			this.$emit('close', false);
		},

		formatLogs() {
			const { statusRole, changedPropertyName, permissions, customers } =
				this.logs || {};

			let items = [];
			if (changedPropertyName === 'Customers') {
				items = this.handleCustomerChanges(items);
				if (!_.isEmpty(permissions)) {
					items = this.handlePermissionsChanges(items);
				}
			}

			if (changedPropertyName === 'Permissions') {
				items = this.handlePermissionsChanges(items);
				if (!_.isEmpty(customers)) {
					items = this.handleCustomerChanges(items);
				}
			}

			if (statusRole) {
				items = this.handleStatusChanges(items, statusRole);
			}
			return items;
		},
		toTitleCase(str) {
			return str
				.replace(/,/g, ', ')
				.split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ');
		},
		insertPermission(items, action, actions) {
			actions.forEach(element => {
				const accessCount = element.access.split(',').length;
				const newValueFormat =
					accessCount === 4
						? `${action} All access`
						: `${action} ${this.toTitleCase(
								element.access
						  )} access`;

				items.push({
					changedPropertyName: `${element.permissionNameFormatted} (Permissions)`,
					newValue: newValueFormat
				});
			});

			return items;
		},
		handlePermissionsChanges(items) {
			const { roleName, action, permissions } = this.logs || {};
			const { added, removed } = permissions;

			if (action === 'Add') {
				items.push({
					changedPropertyName: `${roleName} (Name)`,
					newValue: 'Role created'
				});
			}

			this.insertPermission(items, 'Added', added);
			this.insertPermission(items, 'Removed', removed);

			return items;
		},
		handleCustomerChanges(items) {
			const { customers } = this.logs || {};

			const insertCustomers = (action, actions) => {
				actions.forEach(element => {
					const newItem = {
						changedPropertyName: `${element.CustomerName} (Client)`,
						newValue: action
					};

					items.push(newItem);
				});
			};

			if (!_.isEmpty(customers)) {
				const { added, removed, created } = customers || {};

				if (created) {
					items.push({
						changedPropertyName: 'Role',
						newValue: 'Role created'
					});
					insertCustomers('Added', created.customers);
					this.insertPermission(items, 'Added', created.permissions);
				}
				if (added) {
					insertCustomers('Added', added);
				}
				if (removed) {
					insertCustomers('Removed', removed);
				}
			}

			return items;
		},
		handleStatusChanges(items, statusRole) {
			const formatStatus = statusRole.newStatusValue
				? 'Active'
				: 'Inactive';
			const newItem = {
				changedPropertyName: 'Role Status',
				newValue: `Set to ${formatStatus}`
			};
			items.push(newItem);

			return items;
		}
	},
	watch: {
		page: {
			immediate: true,
			handler() {
				this.fetchDetailRole();
			}
		},
		pageSize: {
			immediate: true,
			handler() {
				this.fetchDetailRole();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.detail-update {
	font-family: $font-family;
	font-size: $bd-txt-font-size-sm;
	color: $neutral-2;
	margin-bottom: $gutter-xl;
}
.table-container--role-detail-history {
	.table__col {
		&--setting {
			&.internal-role {
				width: 196px;
			}
			&.external-role {
				width: 266px;
			}
		}
		&--change {
			&.internal-role {
				width: 316px;
			}
			&.external-role {
				width: 153px;
			}
		}
	}
}

.role-detail {
	&__new-value {
		text-transform: none;
	}
}
</style>
